import { josa } from 'es-hangul'
import i18next, { t } from 'i18next'
import HttpBackend from 'i18next-http-backend'
import { initReactI18next } from 'react-i18next'

const defaultInit = {
  lng: 'custom',
  react: { useSuspense: true },
}

/**
 * i18next를 초기화한다.
 * S3에서 번역 JSON을 로드하지만, 실패하면 빈 리소스로 fallback한다.
 */
export const initI18n = (languagePackUrl?: string | null) => {
  if (languagePackUrl) {
    return i18next
      .use(HttpBackend)
      .use(initReactI18next)
      .init({
        ...defaultInit,
        backend: {
          loadPath: () => languagePackUrl,
        },
      })
      .catch(() => {
        return i18next.use(initReactI18next).init(defaultInit)
      })
  }
  return i18next.use(initReactI18next).init(defaultInit)
}

/**
 * 로그인 이후 받아온 academy 정보로 언어팩을 갱신한다.
 * 최초 진입이 로그인 페이지였던 경우 initI18n 시점엔 academy 가 없어
 * 언어팩이 비어 있으므로, 로그인 완료 직후 호출해 리소스를 채워준다.
 *
 * 이미 초기화된 i18next 인스턴스에 리소스만 추가 → changeLanguage 로
 * react-i18next 가 구독 중인 'languageChanged' 이벤트를 발생시켜 재렌더 트리거.
 */
export const setI18nLanguagePack = async (languagePackUrl?: string | null) => {
  if (!languagePackUrl) return
  if (!i18next.isInitialized) return // initI18n 이 처리할 시점
  // CDN 지연으로 로그인 직후 화면 진입이 블로킹되지 않도록 상한을 둔다.
  // timeout 시 fetch 가 abort 되어 언어팩은 이번 세션에서는 적용되지 않는다 (트레이드오프: 진입 지연 < 키 노출).
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), 5000)
  try {
    const res = await fetch(languagePackUrl, { cache: 'no-cache', signal: controller.signal })
    if (!res.ok) return
    const resources = await res.json()
    i18next.addResourceBundle('custom', 'translation', resources, true, true)
    await i18next.changeLanguage('custom')
  } catch {
    // 실패/timeout 시 기존 리소스 유지
  } finally {
    clearTimeout(timeoutId)
  }
}

/**
 * i18n 번역 결과에 한국어 조사를 자동으로 붙여주는 함수.
 * josaOption이 없으면 t(key)만 반환하고, 있으면 받침에 맞는 조사를 붙여 반환한다.
 */
export const tJosa = (key: string, josaOption?: Parameters<typeof josa>[1]): string => {
  const translated = t(key)
  if (josaOption) {
    return josa(translated, josaOption)
  }
  return translated
}
